import { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Alert,
  Avatar,
  Divider,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import { MainLayout } from '@/components/layout';
import { LoadingSpinner } from '@/components/common';
import { usersApi, type UserInfo } from '@/api';

export function ProfilePage() {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();

  const [user, setUser] = useState<UserInfo | null>(null);
  const [name, setName] = useState('');
  const [avatar, setAvatar] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const response = await usersApi.getMe();
        if (response.success && response.data) {
          setUser(response.data);
          setName(response.data.name || '');
          setAvatar(response.data.avatar || '');
        }
      } catch (err) {
        enqueueSnackbar(t('error.serverError'), { variant: 'error' });
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();
  }, [enqueueSnackbar, t]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;

    setError(null);
    setSaving(true);

    try {
      const response = await usersApi.updateUser(user.id, {
        name,
        avatar: avatar || undefined,
      });
      if (response.success && response.data) {
        setUser(response.data);
        enqueueSnackbar(t('user.profileUpdated'), { variant: 'success' });
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : t('error.serverError');
      setError(message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <MainLayout>
        <LoadingSpinner />
      </MainLayout>
    );
  }

  if (!user) {
    return (
      <MainLayout>
        <Alert severity="error">{t('error.serverError')}</Alert>
      </MainLayout>
    );
  }

  return (
    <MainLayout maxWidth="sm">
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" gutterBottom>
          {t('user.profile')}
        </Typography>
      </Box>

      <Paper sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
          <Avatar src={avatar} sx={{ width: 80, height: 80 }}>
            {name?.[0] || user.email[0]}
          </Avatar>
          <Box>
            <Typography variant="h6">{name || user.email}</Typography>
            <Typography variant="body2" color="text.secondary">
              {user.email}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t(`roles.${user.role.toLowerCase()}`)}
            </Typography>
          </Box>
        </Box>

        <Divider sx={{ my: 2 }} />

        <Box component="form" onSubmit={handleSubmit} noValidate>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <TextField
            margin="normal"
            fullWidth
            id="email"
            label={t('auth.email')}
            value={user.email}
            disabled
          />

          <TextField
            margin="normal"
            fullWidth
            id="name"
            label={t('user.name')}
            name="name"
            autoComplete="name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            disabled={saving}
          />

          <TextField
            margin="normal"
            fullWidth
            id="avatar"
            label={t('user.avatar')}
            name="avatar"
            placeholder="https://..."
            value={avatar}
            onChange={(e) => setAvatar(e.target.value)}
            disabled={saving}
            helperText="URL to avatar image"
          />

          <Box sx={{ mt: 3 }}>
            <Button
              type="submit"
              variant="contained"
              disabled={saving}
            >
              {saving ? t('common.loading') : t('user.updateProfile')}
            </Button>
          </Box>
        </Box>
      </Paper>
    </MainLayout>
  );
}
